import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { TDR } from 'tdr-common';
import getLargeGroupSettings from '../../api/getLargeGroupSettings';
import Notice from '../../icons/notice.svg';
import Stop from '../../icons/stop.svg';
import Timer from '../../icons/timer.svg';
import Wallet from '../../icons/wallet.svg';
import { CheckoutGuardOutletContext } from '../../routes/CheckoutGuard';
import { Policy } from './Policy';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';

const PolicyItemsLGB = React.memo(function () {
	const { checkoutSession, restaurant } = useOutletContext<CheckoutGuardOutletContext>();
	const { t, i18n } = useTranslation();
	const [largeGroupSettings, setLargeGroupSettings] = useState<TDR.LargeGroupSettings | null>(null);

	const { tableId } = checkoutSession;

	useEffect(() => {
		let isMounted = true;

		const fetchLargeGroupSettings = async () => {
			try {
				const settings = await getLargeGroupSettings(tableId);
				if (isMounted) {
					setLargeGroupSettings(settings);
				}
			}
			catch (error) {
				Sentry.captureException(error);
			}
		};

		if (tableId) {
			fetchLargeGroupSettings();
		}

		return () => {
			isMounted = false;
		};
	}, [tableId]);

	if (!largeGroupSettings) {
		return null;
	}

	const { response, cancellationWindow, bookingDeposit, minimumSpend, autoGratuity, gracePeriod, freeformPolicy } =
		largeGroupSettings;

	const currencyConfig = TDR.CurrencyConfig[restaurant?.currency || TDR.Currency.USD];
	const currencySymbol = currencyConfig.symbol;
	const currencyAndAmount = (amount: number) => `${currencyConfig.placement === 'before' ? currencySymbol : ''}${amount}${currencyConfig.placement === 'after' ? currencySymbol : ''}`;
	const depositText = bookingDeposit?.amount
		? `${currencyAndAmount(bookingDeposit.amount)}${bookingDeposit?.perPerson ? '/guest' : ''}`
		: '';
	const minimumSpendText = minimumSpend?.amount
		? `${currencyAndAmount(minimumSpend.amount)}${minimumSpend?.perPerson ? '/guest' : ''}`
		: '';


	return (
		<ul className='p-3'>
			{response > 0 && (
				<li>
					<Policy icon={'Timer'} title={t('policies.booking_request', { defaultValue: 'Booking Request' })}>
						<p>
							{t('policies.booking_request_body', { defaultValue: 'Your group booking will be confirmed within <strong>{{hours}} hours</strong>.', hours: response })}
						</p>
					</Policy>
				</li>
			)}

			<li>
				<Policy icon={Wallet} label='Payment' title={t('policies.payment_processing', { defaultValue: 'Payment Processing' })}>
					<p>{t('policies.payment_processing_body', { defaultValue: 'Your payment will be processed once your booking is confirmed.' })}</p>
				</Policy>
			</li>

			<li>
				<Policy icon={'ImportCircle'} title={t('policies.seating_selection', { defaultValue: 'Seating Selection' })}>
					<p>{t('policies.seating_selection_body', { defaultValue: 'The seating <strong>selection fee is separate</strong> from the food and beverage deposit' })}</p>
				</Policy>
			</li>

			{bookingDeposit?.amount > 0 && (
				<li>
					<Policy icon={'DollarSquare'} title={t('titles.deposit')}>
						<p>{t('policies.deposit_body', { defaultValue: 'A non-refundable deposit of <strong>{{amount}}</strong>, deducted from the final bill at the restaurant.', amount: depositText })}</p>
					</Policy>
				</li>
			)}

			{minimumSpend?.amount > 0 && (
				<li>
					<Policy icon={'Coin1'} title={t('policies.minimum_spend', { defaultValue: 'Minimum spend' })}>
						<p>{t('policies.minimum_spend_body', { defaultValue: 'Your deposit will be deducted from a minimum spend of <strong>{{amount}}</strong> on food and beverage, prior to tax and gratuity, at the restaurant.', amount: minimumSpendText })}</p>
					</Policy>
				</li>
			)}

			{autoGratuity > 0 && (
				<li>
					<Policy icon={'ReceiptDiscount'} title={t('policies.auto_gratuity', { defaultValue: 'Auto-gratuity' })}>
						<p>{t('policies.auto_gratuity_body', { defaultValue: 'For large group bookings, there is an auto-gratuity of <strong>{{percent}}%</strong> applied to your final bill at the restaurant.', percent: autoGratuity })}</p>
					</Policy>
				</li>
			)}

			{cancellationWindow > 0 && (
				<li>
					<Policy icon={Stop} label='Cancellation' title={t('policies.cancellation_window', { defaultValue: 'Cancellation Window' })}>
						<p>{t('policies.cancellation_window_body', { defaultValue: 'Cancellations can be made with no penalty up to <strong>{{hours}} hours</strong> in advance of your booking time.', hours: cancellationWindow })}</p>
					</Policy>
				</li>
			)}

			{gracePeriod > 0 && (
				<li>
					<Policy icon={Timer} label='Grace period' title={t('policies.grace_period', { defaultValue: 'Grace Period' })}>
						<p>{t('policies.grace_period_body', { defaultValue: 'All bookings have a <strong>{{minutes}} minute</strong> grace period from your reserved seating time. Your booking may not be honored if you arrive past the {{minutes}} minute grace period on the day of your reservation.', minutes: gracePeriod })}</p>
					</Policy>
				</li>
			)}

			{!!freeformPolicy && (
				<li>
					<Policy icon={Notice} label='Other policy'>
						<p>{resolveLocalizedText(freeformPolicy, i18n.language)}</p>
					</Policy>
				</li>
			)}
		</ul>
	);
});

PolicyItemsLGB.displayName = 'PolicyItemsLGB';
export default PolicyItemsLGB;
